/**
 * dev 오버레이의 네트워크 로그 — API 호출 기록을 메모리 링버퍼에 담는다.
 *
 * 이 모듈만 `lib/api.ts`·`lib/adminApi.ts`가 참조하므로 **메인 번들에 들어간다.**
 * 그래서 의도적으로 작고, 기본이 **꺼짐**이다 — 오버레이가 마운트될 때만 켜져
 * 평소 방문자에게는 배열 하나가 비어 있는 상태로 남는다.
 *
 * **응답 본문을 담지 않는다.** 시민 문제 원문·연락처가 응답에 실릴 수 있는데,
 * 그걸 버퍼에 넣으면 개인정보가 브라우저 메모리와 "스냅샷 복사" 결과에 따라 나간다.
 * 디버깅에 필요한 것은 어디에·얼마나·어떤 상태로 갔는가이지 본문이 아니다.
 * 본문이 정말 필요하면 브라우저 개발자도구 Network 탭을 쓴다.
 */

export interface DevRequestLog {
  id: number
  method: string
  /** 쿼리 포함 경로. 절대 URL을 만들지 않는다(§10-5와 같은 이유로 습관을 지킨다). */
  path: string
  /** 0은 "응답을 못 받음"(네트워크 실패)이다 — HTTP 상태 0이 아니라 우리 표기다. */
  status: number
  errorCode: string | null
  durationMs: number
  startedAt: string
}

const MAX_ENTRIES = 100

let enabled = false
let seq = 0
const buffer: DevRequestLog[] = []
/** useSyncExternalStore는 같은 상태면 **같은 참조**를 요구한다 — 갱신 때만 새로 만든다. */
let snapshot: readonly DevRequestLog[] = []
const listeners = new Set<() => void>()

export function enableDevNetwork(): void {
  enabled = true
}

export function recordDevRequest(entry: Omit<DevRequestLog, 'id'>): void {
  if (!enabled) return
  buffer.push({ ...entry, id: ++seq })
  if (buffer.length > MAX_ENTRIES) buffer.splice(0, buffer.length - MAX_ENTRIES)
  // 최근 것이 위로 오게 뒤집어 둔다 — 화면이 매 렌더 뒤집으면 참조가 매번 바뀐다.
  snapshot = [...buffer].reverse()
  for (const l of listeners) l()
}

export function clearDevNetwork(): void {
  buffer.length = 0
  snapshot = []
  for (const l of listeners) l()
}

export function subscribeDevNetwork(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function getDevNetworkSnapshot(): readonly DevRequestLog[] {
  return snapshot
}

/**
 * 호출 한 건의 계측을 시작한다. 반환된 함수를 성공·실패 **양쪽에서** 부른다 —
 * 실패만 기록하면 "요청이 나갔는데 응답이 없다"와 "요청 자체가 없다"를 구분할 수 없다.
 */
export function startDevRequest(method: string, path: string): (status: number, errorCode: string | null) => void {
  if (!enabled) return () => {}
  const startedAt = new Date().toISOString()
  const t0 = performance.now()
  return (status, errorCode) => {
    recordDevRequest({
      method,
      path,
      status,
      errorCode,
      durationMs: Math.round(performance.now() - t0),
      startedAt,
    })
  }
}
